import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import BrandsSettings from '../components/settings/BrandsSettings';
import AgencyBrandingSettings from '../components/settings/AgencyBrandingSettings';
import NotificationsSettings from '../components/settings/NotificationsSettings';
import WebhooksIntegration from '../components/settings/WebhooksIntegration';
import McpIntegration from '../components/settings/McpIntegration';
import BillingSettings from '../components/settings/BillingSettings';
import { resolveLimits, resolveEntitlement, canAccessReports } from '@traaker/shared';
import type {
  User,
  UserRole,
  Tenant,
  TierLimits,
} from '@traaker/shared';

const ROLE_LABEL_KEYS: Record<UserRole, string> = {
  owner: 'settings.role.owner',
  admin: 'settings.role.admin',
  member: 'settings.role.member',
  viewer: 'settings.role.viewer',
};

const ROLE_COLORS: Record<UserRole, string> = {
  owner: '#10B981',
  admin: '#7C3AED',
  member: '#5F6470',
  viewer: '#A0A4AE',
};

const TAB_KEYS = ['org', 'brands', 'users', 'billing', 'integrations', 'branding', 'notifications'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** Tabs rendered in the strip, in order. `billing` is deliberately ABSENT: the
 *  Facturation panel is its own sidebar section, so it renders standalone (no
 *  strip at all — see `isBillingPanel`) and a tab here would be a second door.
 *  `?tab=billing` still RESOLVES because it stays in `TAB_KEYS` — keep the two
 *  lists separate, or the sidebar link falls back to the Entreprise tab. */
const STRIP_TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'org', labelKey: 'settings.tab.org' },
  { key: 'brands', labelKey: 'settings.tab.brands' },
  { key: 'users', labelKey: 'settings.tab.users' },
  { key: 'notifications', labelKey: 'settings.tab.notifications' },
  { key: 'integrations', labelKey: 'settings.tab.integrations' },
];

export default function Settings() {
  const { t } = useTranslation();
  const { tenant, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  // White-label tab follows the Reports access rule: agency always, enterprise
  // via the reportsEnabled flag.
  const reportsEnabled = canAccessReports(tenant?.tier, tenant?.settings?.featureFlags?.reportsEnabled);
  // MCP access: tier default (elite/enterprise/agency) unless admin-overridden
  // per tenant via quotaOverrides.webhookAccess — see docs/mcp-server.md.
  const mcpEnabled = !!tenant && resolveLimits(tenant.tier, tenant.settings?.quotaOverrides).webhookAccess;

  const tabParam = (searchParams.get('tab') || 'org') as TabKey;
  const strip = reportsEnabled
    ? [...STRIP_TABS, { key: 'branding' as TabKey, labelKey: 'settings.tab.branding' }]
    : STRIP_TABS;
  // `billing` resolves but isn't in the strip, so an unknown/hidden tab falls
  // back to `org` while a valid one is honoured verbatim.
  const activeTab: TabKey = TAB_KEYS.includes(tabParam) && (tabParam !== 'branding' || reportsEnabled)
    ? tabParam
    : 'org';
  // Facturation renders standalone (own sidebar entry): no strip, own H5 title.
  const isBillingPanel = activeTab === 'billing';
  const activeIdx = Math.max(0, strip.findIndex((tb) => tb.key === activeTab));

  const handleTabChange = (_: unknown, value: number) => {
    const key = strip[value].key;
    setSearchParams(key === 'org' ? {} : { tab: key });
  };

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    api.get<User[]>('/users').then(setUsers).catch(() => {});
  }, []);

  const handleRoleChange = async (userId: string, role: UserRole) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, role } : u)));
    } catch {
      setMessage(t('settings.error.roleChange'));
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch {
      setMessage(t('settings.error.userRemove'));
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t(isBillingPanel ? 'settings.tab.billing' : 'settings.title')}
      </Typography>

      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {/* Facturation is its own sidebar section, so it renders standalone — no
          tab strip of unrelated Settings panels above it. */}
      {!isBillingPanel && (
        <Tabs
          value={activeIdx}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {strip.map((tb) => (
            <Tab key={tb.key} label={t(tb.labelKey)} />
          ))}
        </Tabs>
      )}

      {/* Tab: Organization */}
      {activeTab === 'org' && tenant && <EnterpriseTab tenant={tenant} userCount={users.length} />}

      {/* Tab: Brands & Locations */}
      {activeTab === 'brands' && <BrandsSettings />}

      {/* Tab: Billing (subscribe / upgrade / packs / credits / manage) */}
      {activeTab === 'billing' && tenant && <BillingSettings tenant={tenant} />}

      {/* Tab: Notifications (registry-driven email notifications) */}
      {activeTab === 'notifications' && <NotificationsSettings />}

      {/* Tab: Integrations (webhooks; WordPress + RSS live in the Studio) */}
      {activeTab === 'integrations' && (
        <>
          <WebhooksIntegration />
          <McpIntegration entitled={mcpEnabled} />
        </>
      )}

      {/* Tab: Agency branding (reports white-label) */}
      {activeTab === 'branding' && reportsEnabled && <AgencyBrandingSettings />}

      {/* Tab: Users */}
      {activeTab === 'users' && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {t('settings.users.title', { count: users.length })}
              </Typography>
              {isAdmin && (
                <Button
                  startIcon={<PersonAddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setInviteOpen(true)}
                >
                  {t('settings.users.invite')}
                </Button>
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('settings.users.name')}</TableCell>
                  <TableCell>{t('settings.users.email')}</TableCell>
                  <TableCell>{t('settings.users.role')}</TableCell>
                  {isAdmin && <TableCell align="right">{t('settings.users.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {isAdmin && u.role !== 'owner' ? (
                        <Select
                          value={u.role}
                          size="small"
                          onChange={(e) => handleRoleChange(u.userId, e.target.value as UserRole)}
                          sx={{ minWidth: 130 }}
                        >
                          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
                            <MenuItem key={r} value={r}>{t(ROLE_LABEL_KEYS[r])}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          label={t(ROLE_LABEL_KEYS[u.role])}
                          size="small"
                          sx={{ bgcolor: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role], fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        {u.role !== 'owner' && (
                          <IconButton size="small" onClick={() => handleRemoveUser(u.userId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(u) => {
          setUsers((prev) => [...prev, u]);
          setInviteOpen(false);
        }}
      />
    </Box>
  );
}

// Quota metadata: label + plain-French explanation. `usage` (when set) names a
// live-count source so the row renders a gauge instead of a bare number.
type UsageKey = 'brands' | 'zones' | 'users' | 'competitors';
interface QuotaMeta {
  key: keyof TierLimits;
  labelKey: string;
  descKey: string;
  usage?: UsageKey;
}

// Headline quotas — the ones a user actually feels. Gauges where we have a live count.
const QUOTA_PRIMARY: ReadonlyArray<QuotaMeta> = [
  { key: 'brands', labelKey: 'settings.quota.brands.label', descKey: 'settings.quota.brands.desc', usage: 'brands' },
  { key: 'zones', labelKey: 'settings.quota.zones.label', descKey: 'settings.quota.zones.desc', usage: 'zones' },
  { key: 'users', labelKey: 'settings.quota.users.label', descKey: 'settings.quota.users.desc', usage: 'users' },
  { key: 'competitors', labelKey: 'settings.quota.competitors.label', descKey: 'settings.quota.competitors.desc', usage: 'competitors' },
];

// Brand-perception prompt budget — ONE shared gauge across the 3 axes
// (narrative/authority/referral). Replaced the legacy per-axis rows.

/**
 * Headline quota with a usage gauge. For finite limits with a live `used` count
 * (non-agency), renders a labeled progress bar (amber ≥80%, red at 100%); for
 * unlimited (-1, e.g. agency) renders an "Illimité" pill. Always shows the
 * one-line explanation so the quota is self-describing.
 */
function QuotaGauge({
  meta, limit, used, overridden, agency,
}: {
  meta: { label: string; desc: string; usage?: string };
  limit: number;
  used?: number;
  overridden: boolean;
  agency: boolean;
}) {
  const { t } = useTranslation();
  const unlimited = limit === -1;
  const showBar = !unlimited && !agency && typeof used === 'number';
  const pct = showBar && limit > 0 ? Math.min(100, Math.round((used! / limit) * 100)) : 0;
  const color = pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
          {overridden && <Chip label={t('settings.quota.custom')} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {unlimited
            ? t('settings.quota.unlimited')
            : showBar
              ? <>{used}<Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}> / {limit}</Box></>
              : limit}
        </Typography>
      </Box>
      {showBar && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{ height: 6, borderRadius: 99, mt: 0.75, bgcolor: 'action.hover' }}
        />
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {meta.desc}
      </Typography>
    </Box>
  );
}

/** Feature on/off pill. `alwaysOn` (crawl mode) shows offLabel instead of greying out. */
function CapabilityChip({
  on, label, offLabel, alwaysOn,
}: { on: boolean; label: string; offLabel?: string; alwaysOn?: boolean }) {
  return (
    <Chip
      label={on ? label : alwaysOn ? offLabel ?? label : label}
      size="small"
      color={on ? 'success' : 'default'}
      variant={on ? 'filled' : 'outlined'}
      sx={on ? {} : { opacity: 0.55 }}
    />
  );
}

function EnterpriseTab({ tenant, userCount }: { tenant: Tenant; userCount: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brands } = useBrand();
  // Live tenant-wide usage (competitors + prompts have no client-side count) —
  // ONE call, refreshed when the brand list changes. Null until loaded / on error.
  const [usedByMetric, setUsedByMetric] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ metrics: { metric: string; used: number }[] }>('/metering/usage')
      .then((u) => {
        if (cancelled) return;
        setUsedByMetric(Object.fromEntries((u.metrics ?? []).map((m) => [m.metric, m.used])));
      })
      .catch(() => { if (!cancelled) setUsedByMetric({}); });
    return () => { cancelled = true; };
  }, [brands.length]);
  const limits = resolveLimits(tenant.tier, tenant.settings?.quotaOverrides);
  const overrideKeys = Object.keys(tenant.settings?.quotaOverrides ?? {}) as Array<keyof TierLimits>;
  // Shared prompts gauge (registry metric, not a TierLimits field).
  const promptsLimit = resolveEntitlement('prompts', tenant.tier, {
    override: tenant.settings?.quotaOverrides?.prompts,
    packs: tenant.settings?.activePacks?.prompts,
  }).limit;
  // Tasks gauge — registry metric (tenant-wide, all statuses), enforced on create.
  const tasksLimit = resolveEntitlement('tasks', tenant.tier, {
    override: tenant.settings?.quotaOverrides?.tasks,
    packs: tenant.settings?.activePacks?.tasks,
  }).limit;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Identity — read only */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {t('settings.enterprise.title')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.enterprise.managedInfo')}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('settings.enterprise.nameLabel')}
              value={tenant.name}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              label={t('settings.enterprise.domainLabel')}
              value={tenant.domain}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Abonnement (offre, statut, actions Changer d'offre / Gérer
          l'abonnement) vit entièrement dans la page Facturation
          (settings?tab=billing, entrée dédiée dans le menu — plus d'onglet
          ici) — pas de duplication ici. */}

      {/* Quotas & usage */}
      {(() => {
        const isAgency = tenant.tier === 'agency';
        const totalZones = brands.reduce((s, b) => s + (b.locations?.length ?? 0), 0);
        const usageValue: Record<UsageKey, number | undefined> = {
          brands: brands.length,
          zones: totalZones,
          users: userCount,
          competitors: usedByMetric.competitors,
        };
        return (
          <Card data-tour="quotas-card">
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('settings.quotas.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {isAgency
                  ? t('settings.quotas.agencyDesc')
                  : t('settings.quotas.desc')}
              </Typography>

              {/* Headline quotas — gauges where we have a live count. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {QUOTA_PRIMARY.map((row) => (
                  <QuotaGauge
                    key={row.key}
                    meta={{ label: t(row.labelKey), desc: t(row.descKey), usage: row.usage }}
                    limit={limits[row.key] as number}
                    used={row.usage ? usageValue[row.usage] : undefined}
                    overridden={overrideKeys.includes(row.key)}
                    agency={isAgency}
                  />
                ))}
                {/* Tasks — registry metric (tenant-wide), not in QUOTA_PRIMARY. */}
                <QuotaGauge
                  meta={{ label: t('settings.quota.tasks.label'), desc: t('settings.quota.tasks.desc') }}
                  limit={tasksLimit}
                  used={usedByMetric.tasks}
                  overridden={overrideKeys.includes('tasks' as keyof TierLimits)}
                  agency={isAgency}
                />
              </Box>

              {/* Prompt budget — ONE shared gauge across the 3 BP axes. */}
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>
                {t('settings.quota.promptsBudget')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <QuotaGauge
                  meta={{
                    label: t('settings.quota.prompts.label'),
                    desc: t('settings.quota.prompts.desc'),
                  }}
                  limit={promptsLimit}
                  used={usedByMetric.prompts}
                  overridden={overrideKeys.includes('prompts' as keyof TierLimits)}
                  agency={isAgency}
                />
              </Box>

              {/* Capabilities — feature on/off + modes. */}
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>
                {t('settings.capabilities.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <CapabilityChip on={limits.contentSuggestions} label={t('settings.capability.contentCreation')} />
                <CapabilityChip on={limits.webhookAccess} label={t('settings.capability.apiMcp')} />
                <CapabilityChip on={limits.crawlMode === 'full'} label={t('settings.capability.fullCrawl')} offLabel={t('settings.capability.sitemapCrawl')} alwaysOn />
                <CapabilityChip on={limits.newsWatchMaxProposals !== 0} label={t('settings.capability.newsWatch')} />
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Product tour replay */}
      <Card>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('settings.tour.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings.tour.desc')}
            </Typography>
          </Box>
          <Button
            startIcon={<HelpOutlineIcon />}
            variant="outlined"
            onClick={() => navigate('/', { state: { replayTour: true } })}
          >
            {t('settings.tour.replay')}
          </Button>
        </CardContent>
      </Card>

    </Box>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (user: User) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const user = await api.post<User>('/users/invite', { email, name, role });
      onInvited(user);
      setEmail('');
      setName('');
      setRole('member');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('settings.invite.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label={t('settings.users.name')} value={name} onChange={(e) => setName(e.target.value)} fullWidth />
        <TextField label={t('settings.users.email')} value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
        <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)} fullWidth>
          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
            <MenuItem key={r} value={r}>{t(ROLE_LABEL_KEYS[r])}</MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('settings.invite.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!email || !name}>
          {t('settings.invite.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
